/**
 * What the server's 422 says is wrong, as one line a person can act on.
 *
 * The validation envelope carries `details.issues[]` ({ path, message }) under
 * the headline "Invalid request". The headline alone names nothing, so both API
 * clients (apiFetch's describeApiError and Platform Admin's adminFetch) append
 * this. Zod's own message is kept as the server wrote it; only the field is put
 * into words, and only where the caller asks.
 */

/** Null when the details carry no issues. Capped at three. */
export function describeValidationIssues(
  details: unknown,
  nameField: (path: string) => string = (path) => path,
): string | null {
  const issues = (details as { issues?: unknown } | undefined)?.issues;
  if (!Array.isArray(issues) || issues.length === 0) return null;

  const described = issues
    .filter((i): i is { path?: unknown; message?: unknown } => typeof i === 'object' && i !== null)
    .map((i) => {
      const raw = typeof i.path === 'string' ? i.path : Array.isArray(i.path) ? i.path.join('.') : '';
      const path = raw ? nameField(raw) : '';
      const message = typeof i.message === 'string' ? i.message : '';
      if (!message) return path;
      return path ? `${path}: ${message}` : message;
    })
    .filter((s) => s.length > 0);
  if (described.length === 0) return null;

  const shown = described.slice(0, 3).join(' · ');
  const extra = described.length > 3 ? ` (+${described.length - 3} more)` : '';
  return `${shown}${extra}`;
}

const ACRONYMS: Record<string, string> = { vat: 'VAT', id: 'ID', url: 'URL', ico: 'ICO', eori: 'EORI' };

/** A field path as words: "company.registeredAddressPostcode" is "Registered address postcode". */
export function fieldInWords(path: string): string {
  const segments = path.split('.').filter((s) => s !== '' && !/^\d+$/.test(s));
  const last = segments[segments.length - 1] ?? path;
  const words = last
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[\s_]+/)
    .map((w) => ACRONYMS[w.toLowerCase()] ?? w.toLowerCase());
  const sentence = words.join(' ');
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}
